import { useEffect, useRef, useState } from "react";
import {
  captureSessionContext,
  parseBrowserFallback,
  detectOS,
} from "../telemetry/environment";
import { enqueueMicLog, flushCarryover } from "../telemetry/logQueue";
import {
  acquireMic,
  probeAliveness,
  runSpeechCheck,
} from "../audio-kernel/micKernel";
import { setPreferredMic } from "../audio-kernel/micPreference";

const newAttemptId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `a-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Same parser telemetry/environment.js uses — one rule table, not two
// copies that can drift (the exact bug that made Edge log as Chrome).
const getBrowserInfo = () => {
  const { name, version } = parseBrowserFallback(navigator.userAgent);
  return version ? `${name} ${version}` : name;
};

const getOSInfo = () => detectOS();

// Legacy v1 thresholds on the historic 0-255 byte scale — kept so old
// failure_reason distributions stay comparable across the detection swap.
const LEGACY_MIN_LEVEL = 25;
const LEGACY_MIN_FRAMES = 2;

const determineFailureReason = (hasDetectedSound, consistentSoundCount, maxLevel) => {
  if (maxLevel === 0) return "no_audio_detected";
  if (maxLevel < LEGACY_MIN_LEVEL) return "weak_audio";
  if (consistentSoundCount < LEGACY_MIN_FRAMES) return "inconsistent_audio";
  return "unknown_error";
};

// Transport goes through the telemetry queue (keepalive fetch + pagehide
// beacon + next-load carryover) so aborted tests still report themselves.
// The retry counter is a client-side sessionStorage value, so PASSED clears
// it locally without waiting for a server ack.
const sendMicTestLogs = async (logData, interviewId) => {
  if (logData.test_result === "PASSED") {
    const key = `micTestAttempt_${interviewId}`;
    sessionStorage.removeItem(key);
  }
  enqueueMicLog(logData);
  return { success: true };
};

const snapshotTrack = (t) => ({
  ready_state: t.readyState,
  muted: t.muted,
  enabled: t.enabled,
  label: t.label,
  settings: (() => {
    try {
      const s = t.getSettings();
      return {
        deviceId: s.deviceId,
        echoCancellation: s.echoCancellation,
        noiseSuppression: s.noiseSuppression,
        autoGainControl: s.autoGainControl,
        sampleRate: s.sampleRate,
      };
    } catch (_) {
      return null;
    }
  })(),
});

// Runs the mic-check step in three phases:
//   acquire (kernel, ideal deviceId) → aliveness gate (≤1.5s, silent)
//   → speak phase (RMS detection, early-pass ≥300ms voiced, ≤10s window).
// Detection DSP lives in the audio kernel; this hook owns the state machine,
// telemetry assembly, and the verdict-driven UI state.
export const useMicrophoneTest = ({
  interviewId,
  selectedMic,
  setSelectedMic,
  availableMics,
  permissionsStatus,
  setPermissionsStatus,
  setErrorMessage,
  stopAllStreams,
}) => {
  // P0: starts "idle" — the test runs only when the candidate starts it
  // (auto-start measured silence before people were ready: 50% of first
  // attempts failed with a median 16s retry loop)
  const [micStatus, setMicStatus] = useState("idle");
  const [isTestingMic, setIsTestingMic] = useState(false);
  // Live volume for the on-screen meter (0-100, from the kernel's dB map)
  const [liveLevel, setLiveLevel] = useState(0);
  // P1 verdict-driven UI state
  const [lastVerdict, setLastVerdict] = useState(null);
  const [testedDeviceLabel, setTestedDeviceLabel] = useState(null);
  const [testPhase, setTestPhase] = useState("idle"); // idle|acquiring|aliveness|speaking

  // Stream owned by the test run; stopped on every exit path
  const testStreamRef = useRef(null);
  // Cancel handle for an in-flight kernel speech check
  const speechCheckRef = useRef(null);
  // Live run descriptor: lets cleanupAudio() log ABORTED for a test killed
  // mid-flight (effect refire / unmount) instead of the attempt vanishing.
  const runRef = useRef(null);
  // The device a verdict was issued for — a pass/fail is a statement about
  // ONE physical device at one moment, withdrawn when that stops being true.
  const verdictDeviceRef = useRef(null);

  useEffect(() => {
    captureSessionContext();
    flushCarryover();
  }, []);

  const invalidateVerdict = (message) => {
    verdictDeviceRef.current = null;
    setMicStatus("idle");
    setLastVerdict(null);
    setTestedDeviceLabel(null);
    setLiveLevel(0);
    setErrorMessage(message || null);
  };

  // Withdraw a stale verdict on exactly two triggers: the selection changed
  // after a verdict, or the tested device vanished / was remapped (same
  // deviceId now naming different hardware — the "default" alias does this
  // when the OS default flips). Any other device-list churn leaves the
  // verdict alone, and nothing here ever auto-runs a test — re-arming the
  // Start button off device events is the old A2 retry-storm bug.
  useEffect(() => {
    if (isTestingMic) return;
    if (micStatus !== "verified" && micStatus !== "failed") return;
    const bound = verdictDeviceRef.current;
    if (!bound) return;
    if (selectedMic !== bound.deviceId) {
      invalidateVerdict(null); // candidate switched — silent re-arm
      return;
    }
    const entry = availableMics.find((m) => m.deviceId === selectedMic);
    if (!entry || (bound.label && entry.label && entry.label !== bound.label)) {
      invalidateVerdict(
        "Your tested microphone was disconnected or changed. Please select a microphone and test again."
      );
      // Repoint the selection to a device that exists — leaving it on the
      // vanished id would bind the next verdict to dead hardware (the test
      // itself falls back via `ideal`, and this effect would withdraw the
      // fresh pass immediately).
      const fallback =
        availableMics.find((m) => m.deviceId === "default") || availableMics[0];
      setSelectedMic(fallback ? fallback.deviceId : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMic, availableMics, micStatus, isTestingMic]);

  const emitAbortIfInFlight = (reason) => {
    const run = runRef.current;
    if (!run || !run.active) return;
    run.active = false;
    if (run.stopVisibilityTracking) run.stopVisibilityTracking();
    enqueueMicLog({
      ...run.logData,
      result: "aborted",
      test_result: "ABORTED",
      verdict: "ABORTED",
      abort_reason: reason,
      phase_reached: run.phase,
      test_duration_ms: Date.now() - run.testStartTime,
      attempt_number: run.logData.test_attempt,
    });
  };

  const cleanupAudio = () => {
    // Order matters: mark the run aborted FIRST so a cancelled speech check
    // resolving afterwards sees an inactive run and early-returns (no double log)
    emitAbortIfInFlight("lifecycle_cleanup");
    if (speechCheckRef.current) {
      speechCheckRef.current.cancel("lifecycle_cleanup");
      speechCheckRef.current = null;
    }
    if (testStreamRef.current) {
      testStreamRef.current.getTracks().forEach((t) => t.stop());
      testStreamRef.current = null;
    }
  };

  const getTestAttemptCount = () => {
    const key = `micTestAttempt_${interviewId}`;
    const count = parseInt(sessionStorage.getItem(key) || "0");
    sessionStorage.setItem(key, (count + 1).toString());
    return count + 1;
  };

  const getSelectedMicLabel = () => {
    const selectedDevice = availableMics.find(
      (mic) => mic.deviceId === selectedMic
    );
    return selectedDevice?.label || "Unknown Microphone";
  };

  const testMicrophone = async (trigger) => {
    const testStartTime = Date.now();
    // Non-string trigger = the mic button's onClick event object
    const resolvedTrigger =
      typeof trigger === "string"
        ? trigger
        : micStatus === "failed"
        ? "retry"
        : "button";
    const logData = {
      interview_id: interviewId,
      timestamp: new Date().toISOString(),
      test_attempt: getTestAttemptCount(),
      schema_version: 2,
      detection_engine: "rms-1",
      attempt_id: newAttemptId(),
      trigger: resolvedTrigger,
      device_info: {
        selected_mic_label: getSelectedMicLabel(),
        browser: getBrowserInfo(),
        os: getOSInfo(),
      },
      quick_checks: {
        has_multiple_mics: availableMics.length > 1,
        stream_obtained: false,
        audio_context_working: false,
      },
      devices_v2: availableMics.map((mic) => ({
        id: mic.deviceId,
        label: mic.label,
      })),
    };
    const phases = {};

    // Bind the (eventual) verdict to the device under test — the selection
    // can't change mid-run (picker disabled), so start-time is verdict-time.
    const boundEntry = availableMics.find((m) => m.deviceId === selectedMic);
    verdictDeviceRef.current = {
      deviceId: selectedMic,
      label: boundEntry?.label || null,
    };

    const finishRun = () => {
      if (runRef.current) {
        runRef.current.active = false;
        runRef.current.stopVisibilityTracking();
      }
    };

    const commonFinalFields = () => {
      logData.test_duration_ms = Date.now() - testStartTime;
      logData.attempt_number = logData.test_attempt;
      logData.phases_ms = phases;
      logData.permissions_status = permissionsStatus.microphone;
      logData.browser_info = {
        user_agent: navigator.userAgent,
        platform: navigator.platform,
      };
      logData.selected_microphone = selectedMic
        ? {
            id: selectedMic,
            label: availableMics.find((m) => m.deviceId === selectedMic)?.label,
          }
        : null;
      logData.available_microphones = availableMics.map((mic) => ({
        id: mic.deviceId,
        label: mic.label,
      }));
    };

    const releaseAll = () => {
      cleanupAudio();
      stopAllStreams();
    };

    try {
      setIsTestingMic(true);
      setMicStatus("checking");
      setLastVerdict(null);
      setErrorMessage(null);
      setLiveLevel(0);
      cleanupAudio();
      stopAllStreams(); // the permission-grant stream must not capture concurrently

      // Visibility tracking: background tabs throttle timers — record it.
      const visibility = { hidden_ms: 0, changes: 0 };
      let hiddenSince = document.hidden ? Date.now() : null;
      const onVisibility = () => {
        visibility.changes++;
        if (document.hidden) hiddenSince = Date.now();
        else if (hiddenSince) {
          visibility.hidden_ms += Date.now() - hiddenSince;
          hiddenSince = null;
        }
      };
      document.addEventListener("visibilitychange", onVisibility);
      const stopVisibilityTracking = () => {
        if (hiddenSince) visibility.hidden_ms += Date.now() - hiddenSince;
        document.removeEventListener("visibilitychange", onVisibility);
        logData.visibility = visibility;
      };
      runRef.current = {
        active: true,
        phase: "stream",
        logData,
        testStartTime,
        stopVisibilityTracking,
      };

      // ── PHASE 1: acquire (ideal deviceId — stale ids fall back to default)
      setTestPhase("acquiring");
      let phaseStart = Date.now();
      const acq = await acquireMic({ deviceId: selectedMic || undefined });
      phases.stream = Date.now() - phaseStart;
      if (!acq.ok) throw acq.error; // catch block owns messages + DEVICE_ERROR telemetry
      testStreamRef.current = acq.stream;
      logData.quick_checks.stream_obtained = true;
      logData.track_start = snapshotTrack(acq.track);
      logData.tested_device_label = acq.label;
      setTestedDeviceLabel(acq.label);

      // ── PHASE 2: aliveness gate (silent — no speaking needed)
      runRef.current.phase = "aliveness";
      setTestPhase("aliveness");
      phaseStart = Date.now();
      const aliveness = await probeAliveness(acq.stream, { timeoutMs: 1500 });
      phases.aliveness = Date.now() - phaseStart;
      // Abort race: cleanup may have fired mid-probe (ABORTED already logged)
      if (!runRef.current || !runRef.current.active) return;
      logData.aliveness = aliveness;
      logData.quick_checks.audio_context_working = aliveness.alive !== null;
      console.info(
        `MIC TEST [${acq.label}] aliveness=${aliveness.alive} ` +
        `(${phases.aliveness}ms)${aliveness.error ? ` error=${aliveness.error}` : ""}`
      );

      if (aliveness.alive === false) {
        // Dead pipeline: the device is connected but sending pure zeros.
        // Blocking screen with the device's name — not "speak louder".
        console.warn(`MIC TEST [${acq.label}] verdict=SIGNAL_DEAD -> blocking wall`);
        setIsTestingMic(false);
        setLiveLevel(0);
        setTestPhase("idle");
        setMicStatus("failed");
        setLastVerdict("SIGNAL_DEAD");
        setErrorMessage(null); // the blocking screen owns the copy
        logData.result = "failed";
        logData.test_result = "FAILED";
        logData.verdict = "SIGNAL_DEAD";
        logData.failure_reason = "no_audio_detected";
        logData.phase_reached = "aliveness";
        logData.audio = { first_nonzero_ms: null };
        logData.audio_levels = {
          max_level: 0,
          consistent_sound_count: 0,
          baseline_level: 0,
        };
        logData.error_message =
          "We can't hear anything from your microphone.";
        logData.track_end = {
          ready_state: acq.track.readyState,
          muted: acq.track.muted,
        };
        commonFinalFields();
        finishRun();
        sendMicTestLogs(logData, interviewId).catch((e) =>
          console.error("LOGGING - Failed to send logs:", e)
        );
        releaseAll();
        return;
      }
      // aliveness.alive === null → probe infrastructure failed: FAIL OPEN
      // into the speak phase rather than blocking a possibly-fine mic.

      // ── PHASE 3: speak check (kernel RMS detection)
      runRef.current.phase = "scoring";
      setTestPhase("speaking");
      const check = runSpeechCheck(acq.stream, {
        onLevel: (percent) => setLiveLevel(percent),
      });
      speechCheckRef.current = check;
      const res = await check.promise;
      speechCheckRef.current = null;
      if (res.verdict === "cancelled") return; // ABORTED already logged
      phases.context = res.context_ms;
      phases.scoring = res.elapsed_ms;

      if (res.verdict === "error") {
        console.error(`MIC TEST [${acq.label}] verdict=DEVICE_ERROR error=${res.error}`);
        setIsTestingMic(false);
        setLiveLevel(0);
        setTestPhase("idle");
        setMicStatus("failed");
        setLastVerdict("DEVICE_ERROR");
        setErrorMessage(
          "Microphone couldn't start due to a browser audio restriction. Please try again."
        );
        logData.result = "failed";
        logData.test_result = "FAILED";
        logData.verdict = "DEVICE_ERROR";
        logData.error_code =
          res.error === "audio_context_suspended"
            ? "AudioContextSuspended"
            : String(res.error);
        logData.failure_reason = "audio_context_suspended";
        logData.phase_reached = "scoring";
        logData.error_message =
          "Microphone couldn't start due to a browser audio restriction. Please try again.";
        commonFinalFields();
        finishRun();
        sendMicTestLogs(logData, interviewId).catch((e) =>
          console.error("LOGGING - Failed to send logs:", e)
        );
        releaseAll();
        return;
      }

      // PASSED or NO_SPEECH
      const isWorking = res.verdict === "passed";
      console.info(
        `MIC TEST [${acq.label}] verdict=${isWorking ? "PASSED" : "NO_SPEECH"} ` +
        `voiced_ms=${res.voiced_ms} rms_peak=${res.rms_peak?.toFixed(4)} ` +
        `threshold=${res.threshold?.toFixed(4)}`
      );
      setIsTestingMic(false);
      setLiveLevel(0);
      setTestPhase("idle");
      setMicStatus(isWorking ? "verified" : "failed");
      setLastVerdict(isWorking ? "PASSED" : "NO_SPEECH");

      // Legacy v1 mapping (byte ≈ RMS × 255) so dashboards keep working:
      // max_level keeps its "0 when never voiced" semantics.
      const mappedMaxLevel = Math.min(255, Math.round(res.voiced_rms_peak * 255));
      const mappedBaseline =
        Math.round((res.noise_floor || 0) * 255 * 10) / 10;

      logData.result = isWorking ? "success" : "failed";
      logData.test_result = isWorking ? "PASSED" : "FAILED";
      logData.verdict = isWorking ? "PASSED" : "NO_SPEECH";
      logData.phase_reached = "done";
      logData.audio = {
        raw_peak_byte: Math.min(255, Math.round(res.rms_peak * 255)),
        rms_peak: res.rms_peak,
        voiced_rms_peak: res.voiced_rms_peak,
        noise_floor_rms: res.noise_floor,
        threshold_rms: res.threshold,
        voiced_ms: res.voiced_ms,
        first_voiced_ms: res.first_voiced_ms,
        first_nonzero_ms: res.first_nonzero_ms,
        frames: res.frames,
        frame_interval_avg_ms: res.tick_interval_avg_ms,
        floor_zero: res.floor_zero,
      };
      logData.audio_levels = {
        max_level: mappedMaxLevel,
        consistent_sound_count: res.voiced_frames,
        baseline_level: mappedBaseline,
      };
      logData.track_end = {
        ready_state: acq.track.readyState,
        muted: acq.track.muted,
      };

      if (isWorking) {
        // Persist the RESOLVED device (not the requested ideal) — the device
        // that literally passed is the one the interview should record with.
        let resolvedId = selectedMic || null;
        try {
          resolvedId = acq.track.getSettings().deviceId || resolvedId;
        } catch (_) {}
        if (resolvedId) {
          setPreferredMic(interviewId, {
            deviceId: resolvedId,
            label: acq.label || null,
          });
        }
      } else {
        logData.failure_reason = determineFailureReason(
          res.voiced_frames > 0,
          res.voiced_frames,
          mappedMaxLevel
        );

        const hasMultipleDevices = availableMics.length > 1;
        let failureMessage;
        if (mappedMaxLevel === 0 || res.voiced_frames === 0) {
          failureMessage = hasMultipleDevices
            ? "No audio detected. Please speak aloud and select the correct microphone from your browser settings."
            : "No audio detected. Please speak aloud and check your microphone connection from your browser settings.";
        } else if (
          res.voiced_frames < LEGACY_MIN_FRAMES ||
          mappedMaxLevel <= LEGACY_MIN_LEVEL
        ) {
          failureMessage = hasMultipleDevices
            ? "Weak audio signal. Please speak louder, try different microphone from the browser settings, or check volume settings."
            : "Weak audio signal. Please speak louder and closer, check volume settings, or use external microphone.";
        } else {
          failureMessage = hasMultipleDevices
            ? "Microphone test failed. Please speak clearly during test, try different microphone from the browser settings."
            : "Microphone test failed. Please speak clearly during test, check your microphone connection from your browser settings.";
        }
        logData.error_message = failureMessage;
        setErrorMessage(failureMessage);
      }

      commonFinalFields();
      finishRun();
      sendMicTestLogs(logData, interviewId).catch((error) =>
        console.error("LOGGING - Failed to send logs:", error)
      );
      releaseAll();
    } catch (error) {
      console.error(" MIC TEST - Error occurred:", error);

      setMicStatus("failed");
      setIsTestingMic(false);
      setLiveLevel(0);
      setTestPhase("idle");
      setLastVerdict("DEVICE_ERROR");

      // Honest error mapping: only a real permission denial flips permission
      // state to "denied" — a busy or vanished device is NOT a permissions
      // problem (the old code dead-ended those users at "enable in settings")
      let micErrorMessage = "";
      if (error.name === "NotAllowedError" || error.name === "SecurityError") {
        micErrorMessage +=
          "Permission denied. Please allow microphone access and try again.";
        setPermissionsStatus((prev) => ({ ...prev, microphone: "denied" }));
      } else if (error.name === "NotFoundError") {
        micErrorMessage +=
          "No microphone found. Please connect a microphone and refresh the page.";
      } else if (error.name === "NotReadableError") {
        micErrorMessage +=
          "Your microphone is in use by another app (like Zoom or Teams). Please close it and click the mic to try again.";
      } else if (error.name === "OverconstrainedError") {
        micErrorMessage +=
          "The selected microphone is no longer available. Click the mic to retry with your default microphone.";
      } else {
        micErrorMessage +=
          "Something went wrong while accessing the microphone. Click the mic to try again.";
      }

      if (error.name !== "NotAllowedError" && error.name !== "SecurityError") {
        setErrorMessage(micErrorMessage);
      }

      // Log error with comprehensive troubleshooting data
      const selectedMicInfo =
        availableMics.find((m) => m.deviceId === selectedMic) || null;
      logData.result = "error"; // Backend format
      logData.test_result = "ERROR"; // Frontend format
      logData.test_duration_ms = Date.now() - testStartTime;
      logData.attempt_number = logData.test_attempt; // Backend compatibility
      logData.failure_reason = error.name?.toLowerCase() || "unknown_error";
      logData.permission_error = error.name;
      logData.technical_error = error.message;
      logData.error_message = micErrorMessage;
      logData.verdict = "DEVICE_ERROR";
      logData.error_code = error.name || "unknown";
      logData.phase_reached = runRef.current?.phase || "stream";
      logData.phases_ms = phases;
      if (runRef.current) {
        runRef.current.active = false;
        runRef.current.stopVisibilityTracking();
      }
      logData.selected_microphone = selectedMicInfo
        ? { id: selectedMicInfo.deviceId, label: selectedMicInfo.label }
        : null;
      logData.available_microphones = availableMics.map((mic) => ({
        id: mic.deviceId,
        label: mic.label,
      }));
      logData.error_details = {
        name: error.name,
        message: error.message,
        constraint: error.constraint,
        stack: error.stack?.substring(0, 500),
      };
      sendMicTestLogs(logData, interviewId).catch((logError) =>
        console.error("LOGGING - Failed to send error log:", logError)
      );

      cleanupAudio();
      stopAllStreams();
    }
  };

  return {
    micStatus,
    setMicStatus,
    isTestingMic,
    setIsTestingMic,
    liveLevel,
    lastVerdict,
    testedDeviceLabel,
    testPhase,
    testMicrophone,
    cleanupAudio,
  };
};
